import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { PV } from '@/constants/testIds';
import { ArrowRight, Download, MessageCircle, Calendar, Clock } from 'lucide-react';
import DashboardMockup from './DashboardMockup';
import { setLeadSource } from '@/lib/leadSource';

/**
 * Hero V2 — premium medical software identity
 *
 * Visual structure:
 * 1. Top: subtle light-blue band on the right behind the product preview
 * 2. LEFT (col-span-7): eyebrow + H1 + subtitle + 3 decision-cards + trust strip
 * 3. RIGHT (col-span-5): DashboardMockup at larger scale
 * 4. Bottom: trust strip with 4 sober chips (Italian software, FSE 2.0, demo, pre-order)
 *
 * The 3 decision cards replace the 3-button row. They feel more premium and
 * give equal visual weight to demo, brochure and WhatsApp — but with clear
 * primary/secondary/tertiary hierarchy.
 */
export const Hero = () => {
  const { t, locale } = useLanguage();
  const m = t.hero.manifesto;

  const cards = locale === 'en'
    ? [
        {
          to: '/bookacall',
          source: 'hero-card-demo',
          icon: Calendar,
          variant: 'primary',
          title: 'Book a demo',
          sub: '30 minutes · zero commitment',
          cta: 'Choose a slot',
          testId: 'pv-hero-card-demo',
        },
        {
          to: '/brochure',
          source: 'hero-card-brochure',
          icon: Download,
          variant: 'soft',
          title: 'Download brochure',
          sub: 'Full PDF · instant',
          cta: 'Get the PDF',
          testId: 'pv-hero-card-brochure',
        },
        {
          to: 'https://wa.me/393520526856?text=Ciao!%20Vorrei%20informazioni%20su%20Prime%20Vision.',
          external: true,
          source: 'hero-card-whatsapp',
          icon: MessageCircle,
          variant: 'whatsapp',
          title: 'WhatsApp',
          sub: 'Direct · fastest reply',
          cta: 'Message us',
          testId: 'pv-hero-card-whatsapp',
        },
      ]
    : [
        {
          to: '/bookacall',
          source: 'hero-card-demo',
          icon: Calendar,
          variant: 'primary',
          title: 'Prenota una demo',
          sub: '30 minuti · zero impegno',
          cta: 'Scegli uno slot',
          testId: 'pv-hero-card-demo',
        },
        {
          to: '/brochure',
          source: 'hero-card-brochure',
          icon: Download,
          variant: 'soft',
          title: 'Scarica la brochure',
          sub: 'PDF completo · subito',
          cta: 'Ricevi il PDF',
          testId: 'pv-hero-card-brochure',
        },
        {
          to: 'https://wa.me/393520526856?text=Ciao!%20Vorrei%20informazioni%20su%20Prime%20Vision.',
          external: true,
          source: 'hero-card-whatsapp',
          icon: MessageCircle,
          variant: 'whatsapp',
          title: 'WhatsApp',
          sub: 'Contatto diretto',
          cta: 'Scrivici',
          testId: 'pv-hero-card-whatsapp',
        },
      ];

  const trustStrip = locale === 'en'
    ? ['Italian software', 'Compliant FSE 2.0 flow', '30-min demo', 'Pre-order 2027']
    : ['Software italiano', 'FSE 2.0 a norma', 'Demo 30 minuti', 'Pre-order 2027'];

  return (
    <section id="top" className="relative overflow-hidden bg-white pt-24 md:pt-28 pb-16 md:pb-20">
      {/* Soft blue medical band — behind the product preview */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 hidden lg:block w-[55%] bg-gradient-to-br from-[#EAF2F8] via-[#F4F9FC] to-white"
      />
      {/* Tiny grid pattern overlay on the blue band — barely visible */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 hidden lg:block w-[55%] opacity-[0.30]"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(27,127,184,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(27,127,184,0.08) 1px, transparent 1px)',
          backgroundSize: '36px 36px',
        }}
      />

      <div className="pv-container relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-start">
          {/* LEFT — content */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-pv-teal" />
              <span className="text-[11px] md:text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                {locale === 'en' ? 'Ophthalmology practice software · 2027' : 'Software gestionale oculistico · 2027'}
              </span>
            </div>

            <h1
              data-testid={PV.heroTitle}
              className="mt-6 md:mt-8 font-heading tracking-normal text-pv-ink"
            >
              {/* LINE 1 — big, primary statement */}
              <span className="block font-bold leading-[1.05] text-[34px] sm:text-[44px] md:text-[52px] lg:text-[60px]">
                {m.line1_a}{' '}
                <span className="text-pv-teal">{m.line1_b}</span>
              </span>

              {/* LINE 2 — elegant, smaller, slate */}
              <span className="block mt-3 md:mt-4 font-semibold leading-[1.20] text-[18px] sm:text-[22px] md:text-[26px] lg:text-[28px] text-slate-700">
                {m.line2}
              </span>
            </h1>

            <p
              data-testid={PV.heroSubtitle}
              className="mt-6 md:mt-7 text-slate-600 text-[16px] md:text-[18px] leading-[1.7] max-w-xl"
            >
              {t.hero.subtitle}
            </p>

            {/* DECISION CARDS — 3 paths to convert, premium look */}
            <div
              className="mt-10 md:mt-12 grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 max-w-2xl"
              role="group"
              aria-label="Choose how to start"
            >
              {cards.map((card) => {
                const Icon = card.icon;
                const styles = {
                  primary:
                    'bg-gradient-to-br from-[#FF7A47] to-[#FF6B35] text-white border border-orange-400/30 shadow-[0_8px_22px_-8px_rgba(255,107,53,0.45)]',
                  soft:
                    'bg-white text-pv-ink border border-slate-200 hover:border-pv-teal/45 shadow-[0_2px_8px_-4px_rgba(15,23,42,0.06)]',
                  whatsapp:
                    'bg-white text-pv-ink border border-slate-200 hover:border-[#1FB755]/55 shadow-[0_2px_8px_-4px_rgba(15,23,42,0.06)]',
                }[card.variant];

                const iconWrap = {
                  primary: 'bg-white/20 text-white',
                  soft: 'bg-pv-ice text-pv-teal',
                  whatsapp: 'bg-emerald-50 text-emerald-600',
                }[card.variant];

                const subClass = card.variant === 'primary'
                  ? 'text-white/80'
                  : 'text-slate-500';

                const ctaArrow = card.variant === 'primary' ? 'text-white' : 'text-pv-teal';

                const inner = (
                  <>
                    <div className="flex items-start justify-between">
                      <span className={`inline-flex items-center justify-center w-9 h-9 rounded-xl ${iconWrap}`}>
                        <Icon className="w-4 h-4" />
                      </span>
                      <ArrowRight className={`w-4 h-4 mt-2 ${ctaArrow} transition-transform group-hover:translate-x-1`} />
                    </div>
                    <div className="mt-4">
                      <div className="font-heading font-bold text-[15px] md:text-[16px] leading-tight">{card.title}</div>
                      <div className={`mt-1 text-[12.5px] leading-snug ${subClass}`}>{card.sub}</div>
                    </div>
                    <div className={`mt-4 text-[12px] font-bold uppercase tracking-[0.12em] ${card.variant === 'primary' ? 'text-white/90' : 'text-pv-teal'}`}>
                      {card.cta}
                    </div>
                  </>
                );

                const className = `group relative flex flex-col rounded-2xl p-5 md:p-5 transition-all duration-200 hover:-translate-y-[2px] ${styles}`;

                return card.external ? (
                  <a
                    key={card.testId}
                    href={card.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={card.testId}
                    onClick={() => setLeadSource(card.source)}
                    className={className}
                  >
                    {inner}
                  </a>
                ) : (
                  <Link
                    key={card.testId}
                    to={card.to}
                    data-testid={card.testId}
                    onClick={() => setLeadSource(card.source)}
                    className={className}
                  >
                    {inner}
                  </Link>
                );
              })}
            </div>

            {/* TRUST STRIP — 4 sober chips */}
            <div className="mt-10 md:mt-12 flex flex-wrap items-center gap-2 md:gap-3 max-w-2xl">
              {trustStrip.map((chip) => (
                <span
                  key={chip}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-pv-ice/70 border border-pv-teal/15 text-[11.5px] font-semibold text-[#0F5E89]"
                >
                  <span className="inline-block w-1 h-1 rounded-full bg-pv-teal" />
                  {chip}
                </span>
              ))}
            </div>

            <p className="mt-6 text-[12.5px] md:text-[13px] text-slate-500 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              {t.hero.trust_label}
            </p>
          </div>

          {/* RIGHT — product preview floats over the blue band */}
          <div className="lg:col-span-5 hidden lg:block">
            <div
              data-testid={PV.heroMockup}
              className="relative lg:mt-4"
            >
              <DashboardMockup />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
